import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  ReferenceLine, Area, ComposedChart
} from 'recharts';
import {
  Zap, Power, Wifi, WifiOff, Activity, AlertTriangle, CheckCircle,
  Sun, Moon, Settings, TrendingUp, DollarSign,
  Lightbulb, ToggleLeft, ToggleRight, RefreshCw, Gauge
} from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const WS_URL = process.env.REACT_APP_WS_URL || 'ws://localhost:8000/ws/telemetry';
const TELEMETRY_POLL_INTERVAL = 2000;  
const AUDIT_REFRESH_INTERVAL = 30000;  

const formatINR = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
};

const formatPower = (watts) => {
  if (watts >= 1000) return `${(watts / 1000).toFixed(2)} kW`;
  return `${watts.toFixed(1)} W`;
};

const getGradeColor = (grade) => {
  const colors = {
    'A+': '#10b981', 'A': '#22c55e', 'B': '#84cc16',
    'C': '#eab308', 'D': '#ef4444', 'N/A': '#6b7280'
  };
  return colors[grade] || colors['N/A'];
};

const isPeakHours = () => {
  const hour = new Date().getHours();
  return hour >= 19 && hour < 22;
};

// =============================================================================
// CUSTOM HOOK: WEBSOCKET
// =============================================================================

const useWebSocket = (url, onMessage) => {
  const ws = useRef(null);
  const reconnectTimeout = useRef(null);
  const [isConnected, setIsConnected] = useState(false);

  const connect = useCallback(() => {
    try {
      ws.current = new WebSocket(url);
      ws.current.onopen = () => setIsConnected(true);
      ws.current.onmessage = (event) => {
        try { onMessage(JSON.parse(event.data)); } 
        catch (e) { console.error('WebSocket parse error:', e); }
      };
      ws.current.onclose = () => {
        setIsConnected(false);
        reconnectTimeout.current = setTimeout(connect, 3000);
      };
    } catch (error) {
      reconnectTimeout.current = setTimeout(connect, 3000);
    }
  }, [url, onMessage]);

  useEffect(() => {
    connect();
    return () => {
      if (ws.current) {
        // FIX APPLIED: Clean socket kill to prevent React Strict mode duplication
        ws.current.onclose = null; 
        ws.current.close();
      }
      if (reconnectTimeout.current) clearTimeout(reconnectTimeout.current);
    };
  }, [connect]);

  return isConnected;
};

// =============================================================================
// UI COMPONENTS
// =============================================================================

const StatusHeader = ({ mqttConnected, mlMode, isPeak }) => (
  <div className="status-header">
    <div className="status-item">
      {mqttConnected ? <><Wifi className="icon success" /> <span>MQTT Connected</span></> : <><WifiOff className="icon error" /> <span>MQTT Disconnected</span></>}
    </div>
    <div className="status-item">
      {mlMode ? <><Settings className="icon active" /> <span>ML Mode Active</span></> : <><Settings className="icon" /> <span>Manual Mode</span></>}
    </div>
    <div className="status-item">
      {isPeak ? <><Moon className="icon warning" /> <span>Peak Hours (₹↑)</span></> : <><Sun className="icon success" /> <span>Off-Peak Hours</span></>}
    </div>
  </div>
);

const PowerDisplayCard = ({ telemetry }) => {
  const power = telemetry?.power_w || 0;
  const voltage = telemetry?.voltage_v || 0;
  const current = telemetry?.current_a || 0;
  const costPerHour = telemetry?.live_cost_inr_per_hour || 0;
  const powerLevel = power < 50 ? 'low' : power < 150 ? 'medium' : 'high';

  return (
    <div className="card power-card">
      <div className="card-header"><Zap className="icon" /><h2>Live Power Consumption</h2></div>
      <div className="power-display">
        <div className={`power-value ${powerLevel}`}>{formatPower(power)}</div>
        <div className="power-label">Real-Time Power</div>
      </div>
      <div className="power-details">
        <div className="detail-item"><Gauge className="icon small" /><span>{voltage.toFixed(1)} V</span></div>
        <div className="detail-item"><Activity className="icon small" /><span>{(current * 1000).toFixed(0)} mA</span></div>
        <div className="detail-item cost"><DollarSign className="icon small" /><span>{formatINR(costPerHour)}/hr</span></div>
      </div>
      {telemetry?.appliance && telemetry.appliance !== 'none' && (
        <div className="active-appliance"><Lightbulb className="icon" /><span>Active: {telemetry.appliance}</span></div>
      )}
    </div>
  );
};

const RelayControlPanel = ({ relayStates, onToggle, onModeChange, mlMode, onAllControl, mlTrained, onTrainML }) => (
  <div className="card relay-card">
    <div className="card-header"><Power className="icon" /><h2>Appliance Control</h2></div>
    <div className="mode-toggle">
      <span className={!mlMode ? 'active' : ''}>Manual</span>
      <button className={`toggle-btn ${mlMode ? 'active' : ''}`} onClick={() => onModeChange(!mlMode)}>
        {mlMode ? <ToggleRight size={32} /> : <ToggleLeft size={32} />}
      </button>
      <span className={mlMode ? 'active' : ''}>ML Auto</span>
    </div>
    <div className="relay-grid">
      {relayStates.map((relay, index) => (
        <div key={index} className={`relay-item ${relay.state === 'ON' ? 'on' : 'off'}`}>
          <div className="relay-info">
            <Lightbulb className={`icon ${relay.state === 'ON' ? 'active' : ''}`} />
            <span className="relay-name">{relay.name || `Relay ${index + 1}`}</span>
            {relay.essential && <span className="essential-badge">Essential</span>}
          </div>
          <button className={`relay-toggle ${relay.state === 'ON' ? 'on' : 'off'}`} onClick={() => onToggle(index, relay.state !== 'ON')} disabled={mlMode}>
            {relay.state}
          </button>
        </div>
      ))}
    </div>
    <div className="bulk-controls">
      <button className="btn btn-success" onClick={() => onAllControl(true)} disabled={mlMode}>All ON</button>
      <button className="btn btn-danger" onClick={() => onAllControl(false)} disabled={mlMode}>All OFF</button>
    </div>
    {mlMode && (
      <div style={{marginTop: '1rem', padding: '1rem', background: 'rgba(59, 130, 246, 0.1)', borderRadius: '8px'}}>
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem', fontSize: '0.875rem'}}>
          <span style={{color: '#60a5fa', fontWeight: '600'}}>Predictive ML Status:</span>
          <span style={{color: mlTrained ? '#10b981' : '#f59e0b', fontWeight: '600'}}>{mlTrained ? 'Trained & Active' : 'Needs Training'}</span>
        </div>
        <button 
          onClick={onTrainML} 
          style={{width: '100%', padding: '0.5rem', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', transition: 'background 0.3s'}}
          onMouseOver={(e) => e.target.style.background = '#2563eb'}
          onMouseOut={(e) => e.target.style.background = '#3b82f6'}
        >
          Train Predictive Model
        </button>
      </div>
    )}
  </div>
);

const AuditCard = ({ audit, isLoading }) => {
  if (isLoading) return <div className="card audit-card loading"><RefreshCw className="icon spinning" /><span>Loading audit data...</span></div>;
  if (!audit) return <div className="card audit-card empty"><AlertTriangle className="icon" /><span>No audit data available</span></div>;

  const gradeColor = getGradeColor(audit.efficiency_grade);
  return (
    <div className="card audit-card">
      <div className="card-header"><TrendingUp className="icon" /><h2>Energy Audit Report</h2><span className="audit-period">Last {audit.audit_period?.hours || 24}h</span></div>
      <div className="grade-display" style={{ borderColor: gradeColor }}>
        <div className="grade-label">Efficiency Grade</div>
        <div className="grade-value" style={{ color: gradeColor }}>{audit.efficiency_grade}</div>
      </div>
      <div className="audit-metrics">
        <div className="metric"><div className="metric-label">Total Energy</div><div className="metric-value">{audit.total_energy_kwh?.toFixed(3)} kWh</div></div>
        <div className="metric"><div className="metric-label">Total Cost</div><div className="metric-value">{formatINR(audit.total_cost_inr)}</div></div>
        <div className="metric"><div className="metric-label">Peak Usage</div><div className="metric-value warning">{audit.peak_usage_percentage?.toFixed(1)}%</div></div>
        <div className="metric"><div className="metric-label">Vampire Draw</div><div className="metric-value error">{formatINR(audit.vampire_draw_cost_inr)}</div></div>
      </div>
      {audit.recommendations && audit.recommendations.length > 0 && (
        <div className="recommendations">
          <h3>Recommendations</h3>
          <ul>{audit.recommendations.map((rec, index) => <li key={index}><CheckCircle className="icon small" /><span>{rec}</span></li>)}</ul>
        </div>
      )}
    </div>
  );
};

const PowerChart = ({ data }) => (
  <div className="card chart-card">
    <div className="card-header"><Activity className="icon" /><h2>Power Consumption Timeline</h2></div>
    <div className="chart-container">
      <ResponsiveContainer width="100%" height={300}>
        <ComposedChart data={data}>
          <defs>
            <linearGradient id="powerGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
              <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.1}/>
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
          <XAxis dataKey="time" stroke="#9ca3af" tick={{ fontSize: 12 }} />
          <YAxis stroke="#9ca3af" tick={{ fontSize: 12 }} label={{ value: 'Watts', angle: -90, position: 'insideLeft', fill: '#9ca3af' }} />
          <Tooltip contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }} labelStyle={{ color: '#f3f4f6' }} />
          <Area type="monotone" dataKey="power" stroke="#3b82f6" fill="url(#powerGradient)" strokeWidth={2} />
          <Line type="monotone" dataKey="power" stroke="#3b82f6" dot={false} strokeWidth={2} />
          <ReferenceLine y={100} stroke="#ef4444" strokeDasharray="5 5" label={{ value: 'ML Threshold', fill: '#ef4444', fontSize: 12 }} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  </div>
);

const TariffCard = ({ tariff }) => {
  if (!tariff) return null;
  return (
    <div className="card tariff-card">
      <div className="card-header"><DollarSign className="icon" /><h2>Session Billing</h2></div>
      <div className="tariff-total"><span className="total-label">Total Amount</span><span className="total-value">{formatINR(tariff.total_amount)}</span></div>
      <div className="tariff-breakdown">
        <div className="breakdown-item"><span>Energy ({tariff.energy_kwh?.toFixed(3)} kWh)</span><span>{formatINR(tariff.energy_charges)}</span></div>
        <div className="breakdown-item"><span>Fixed Charges</span><span>{formatINR(tariff.fixed_charges)}</span></div>
        <div className="breakdown-item"><span>Electricity Duty (5%)</span><span>{formatINR(tariff.electricity_duty)}</span></div>
      </div>
      {tariff.slab_breakdown && tariff.slab_breakdown.length > 0 && (
        <div className="slab-details">
          <h4>Slab Breakdown</h4>
          {tariff.slab_breakdown.map((slab, index) => (
            <div key={index} className="slab-item">
              <span>{slab.slab_name}</span><span>{slab.units} units × ₹{slab.rate} = {formatINR(slab.amount)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// =============================================================================
// MAIN APP ARCHITECTURE
// =============================================================================

const App = () => {
  const [telemetry, setTelemetry] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [systemStatus, setSystemStatus] = useState(null);
  const [mlMode, setMlMode] = useState(false);
  const [relayStates, setRelayStates] = useState([
    { name: 'demo_bulb_1', state: 'OFF', essential: false },
    { name: 'demo_bulb_2', state: 'OFF', essential: false },
    { name: 'demo_bulb_3', state: 'OFF', essential: true },
    { name: 'auxiliary', state: 'OFF', essential: false }
  ]);
  const [audit, setAudit] = useState(null);
  const [tariff, setTariff] = useState(null);
  const [auditLoading, setAuditLoading] = useState(true);
  const [mlTrained, setMlTrained] = useState(false);

  const handleWebSocketMessage = useCallback((data) => {
    setTelemetry(data);
    if (data.relay_states) setRelayStates(data.relay_states);
    if (data.ml_mode !== undefined) setMlMode(data.ml_mode);
    setChartData(prev => [ ...prev, { time: new Date().toLocaleTimeString('en-IN', { hour12: false }), power: data.power_w || 0, voltage: data.voltage_v || 0 } ].slice(-60));
  }, []);

  const wsConnected = useWebSocket(WS_URL, handleWebSocketMessage);

  const fetchTelemetry = async () => { try { const res = await axios.get(`${API_BASE_URL}/api/telemetry/current`); handleWebSocketMessage(res.data); } catch (e) {} };
  const fetchSystemStatus = async () => { try { const res = await axios.get(`${API_BASE_URL}/api/status`); setSystemStatus(res.data); setMlMode(res.data.ml_mode_active); setMlTrained(res.data.ml_model_trained); } catch (e) {} };
  const fetchAudit = async () => { try { setAuditLoading(true); const res = await axios.get(`${API_BASE_URL}/api/audit?hours=24`); setAudit(res.data); } catch (e) {} finally { setAuditLoading(false); } };
  const fetchTariff = async () => { try { const res = await axios.get(`${API_BASE_URL}/api/tariff/current-session`); setTariff(res.data); } catch (e) {} };

  const handleRelayToggle = async (index, newState) => {
    try { await axios.post(`${API_BASE_URL}/api/control/relay`, { command: 'set_relay', appliance_index: index, state: newState }); setRelayStates(prev => { const updated = [...prev]; updated[index] = { ...updated[index], state: newState ? 'ON' : 'OFF' }; return updated; }); } catch (e) { alert('Failed to control relay.'); }
  };

  const handleModeChange = async (newMlMode) => {
    try { await axios.post(`${API_BASE_URL}/api/control/mode?ml_mode=${newMlMode}`); setMlMode(newMlMode); } catch (e) { alert('Failed to change mode.'); }
  };

  const handleAllControl = async (state) => {
    try { await axios.post(`${API_BASE_URL}/api/control/relay`, { command: 'set_all', state: state }); setRelayStates(prev => prev.map(relay => ({ ...relay, state: state ? 'ON' : 'OFF' }))); } catch (e) { alert('Failed to control relays.'); }
  };

  const handleTrainML = async () => {
    try {
      alert("Training started in background. Please wait a few seconds...");
      await axios.post(`${API_BASE_URL}/api/ml/train`);
      setTimeout(async () => {
        const res = await axios.get(`${API_BASE_URL}/api/ml/status`);
        if(res.data.is_trained) {
           setMlTrained(true);
           alert("ML Model trained successfully! Preemptive shedding is now active.");
        } else {
           alert("Training failed. Ensure there is enough historical data in the database.");
        }
      }, 3000);
    } catch (e) { alert('Failed to start training.'); }
  };

  useEffect(() => { fetchSystemStatus(); fetchAudit(); fetchTariff(); }, []);
  useEffect(() => { let interval; if (!wsConnected) { interval = setInterval(fetchTelemetry, TELEMETRY_POLL_INTERVAL); } return () => clearInterval(interval); }, [wsConnected]);
  useEffect(() => { const interval = setInterval(() => { fetchAudit(); fetchTariff(); }, AUDIT_REFRESH_INTERVAL); return () => clearInterval(interval); }, []);

  return (
    <div className="app">
      <header className="app-header">
        <div className="logo"><Zap className="logo-icon" /><h1>Smart Energy Monitor</h1></div>
        <StatusHeader mqttConnected={systemStatus?.mqtt_connected || wsConnected} mlMode={mlMode} isPeak={isPeakHours()} />
      </header>

      <main className="app-main">
        <div className="dashboard-row">
          <PowerDisplayCard telemetry={telemetry} />
          <RelayControlPanel relayStates={relayStates} onToggle={handleRelayToggle} onModeChange={handleModeChange} mlMode={mlMode} onAllControl={handleAllControl} mlTrained={mlTrained} onTrainML={handleTrainML} />
        </div>
        <PowerChart data={chartData} />
        <div className="dashboard-row">
          <AuditCard audit={audit} isLoading={auditLoading} />
          <TariffCard tariff={tariff} />
        </div>
      </main>

      <footer className="app-footer">
        <p>Smart Energy Monitoring System v1.0.0 | Engineering Major Project</p>
        <p>Real-time IoT Dashboard with ML-based Load Scheduling</p>
      </footer>

      {/* FIX APPLIED: Appended the full, completed CSS Block */}
      <style>{`
        * { margin: 0; padding: 0; box-sizing: border-box; }
        .app { min-height: 100vh; background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%); color: #f1f5f9; font-family: 'Inter', -apple-system, sans-serif; }
        .app-header { display: flex; justify-content: space-between; align-items: center; padding: 1rem 2rem; background: rgba(15, 23, 42, 0.8); border-bottom: 1px solid #334155; backdrop-filter: blur(10px); }
        .logo { display: flex; align-items: center; gap: 0.75rem; }
        .logo-icon { width: 32px; height: 32px; color: #3b82f6; }
        .logo h1 { font-size: 1.5rem; font-weight: 700; background: linear-gradient(90deg, #3b82f6, #8b5cf6); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }
        .status-header { display: flex; gap: 1.5rem; }
        .status-item { display: flex; align-items: center; gap: 0.5rem; padding: 0.5rem 1rem; background: rgba(51, 65, 85, 0.5); border-radius: 9999px; font-size: 0.875rem; }
        .icon { width: 18px; height: 18px; color: #94a3b8; }
        .icon.success { color: #10b981; } .icon.error { color: #ef4444; } .icon.warning { color: #f59e0b; }
        .icon.active { color: #3b82f6; animation: pulse 2s infinite; } .icon.small { width: 14px; height: 14px; } .icon.spinning { animation: spin 1s linear infinite; }
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        
        .app-main { padding: 1.5rem 2rem; display: flex; flex-direction: column; gap: 1.5rem; }
        .dashboard-row { display: grid; grid-template-columns: repeat(auto-fit, minmax(400px, 1fr)); gap: 1.5rem; }
        .card { background: linear-gradient(145deg, #1e293b, #0f172a); border: 1px solid #334155; border-radius: 16px; padding: 1.5rem; box-shadow: 0 10px 40px rgba(0,0,0,0.3); }
        .card-header { display: flex; align-items: center; gap: 0.75rem; margin-bottom: 1.5rem; padding-bottom: 1rem; border-bottom: 1px solid #334155; }
        .card-header h2 { font-size: 1.125rem; font-weight: 600; color: #e2e8f0; } .card-header .icon { color: #3b82f6; }
        
        .power-card .power-display { text-align: center; padding: 2rem 0; }
        .power-value { font-size: 3.5rem; font-weight: 700; font-family: monospace; margin-bottom: 0.5rem; }
        .power-value.low { color: #10b981; } .power-value.medium { color: #f59e0b; } .power-value.high { color: #ef4444; }
        .power-label { color: #94a3b8; font-size: 0.875rem; text-transform: uppercase; letter-spacing: 0.1em; }
        .power-details { display: flex; justify-content: space-around; padding: 1rem 0; border-top: 1px solid #334155; border-bottom: 1px solid #334155; }
        .detail-item { display: flex; align-items: center; gap: 0.5rem; color: #cbd5e1; } .detail-item.cost { color: #10b981; font-weight: 600; }
        .active-appliance { display: flex; align-items: center; justify-content: center; gap: 0.5rem; margin-top: 1rem; padding: 0.75rem; background: rgba(59, 130, 246, 0.1); border-radius: 8px; color: #60a5fa; }
        
        .mode-toggle { display: flex; align-items: center; justify-content: center; gap: 1rem; padding: 1rem; margin-bottom: 1rem; background: rgba(51, 65, 85, 0.3); border-radius: 12px; }
        .mode-toggle span { color: #64748b; font-weight: 500; transition: color 0.3s; } .mode-toggle span.active { color: #3b82f6; }
        .toggle-btn { background: none; border: none; cursor: pointer; color: #64748b; transition: color 0.3s; } .toggle-btn.active { color: #3b82f6; }
        .relay-grid { display: grid; gap: 0.75rem; }
        .relay-item { display: flex; justify-content: space-between; align-items: center; padding: 1rem; background: rgba(51, 65, 85, 0.3); border-radius: 10px; border: 1px solid transparent; transition: all 0.3s; }
        .relay-item.on { border-color: #10b981; background: rgba(16, 185, 129, 0.1); }
        .relay-info { display: flex; align-items: center; gap: 0.75rem; }
        .relay-info .icon { color: #64748b; } .relay-info .icon.active { color: #fbbf24; filter: drop-shadow(0 0 8px #fbbf24); }
        .relay-name { font-weight: 500; color: #e2e8f0; }
        .essential-badge { font-size: 0.625rem; padding: 0.125rem 0.5rem; background: #3b82f6; border-radius: 9999px; color: white; text-transform: uppercase; }
        .relay-toggle { padding: 0.5rem 1rem; border: none; border-radius: 6px; font-weight: 600; cursor: pointer; transition: all 0.3s; }
        .relay-toggle.on { background: #10b981; color: white; } .relay-toggle.off { background: #475569; color: #94a3b8; } .relay-toggle:disabled { opacity: 0.5; cursor: not-allowed; }
        .bulk-controls { display: flex; gap: 1rem; margin-top: 1rem; }
        .btn { flex: 1; padding: 0.75rem; border: none; border-radius: 8px; font-weight: 600; cursor: pointer; transition: all 0.3s; } .btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .btn-success { background: #10b981; color: white; } .btn-success:hover:not(:disabled) { background: #059669; }
        .btn-danger { background: #ef4444; color: white; } .btn-danger:hover:not(:disabled) { background: #dc2626; }
        .ml-notice { display: flex; align-items: center; justify-content: center; gap: 0.5rem; margin-top: 1rem; padding: 0.75rem; background: rgba(245, 158, 11, 0.1); border-radius: 8px; color: #f59e0b; font-size: 0.875rem; }
        
        .audit-card.loading, .audit-card.empty { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 1rem; min-height: 300px; color: #94a3b8; }
        .grade-display { display: flex; flex-direction: column; align-items: center; padding: 1.5rem; border: 2px dashed; border-radius: 12px; margin-bottom: 1.5rem; }
        .grade-label { font-size: 0.875rem; text-transform: uppercase; letter-spacing: 0.1em; margin-bottom: 0.5rem; }
        .grade-value { font-size: 4rem; font-weight: 800; line-height: 1; }
        .audit-metrics { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; margin-bottom: 1.5rem; }
        .metric { background: rgba(51, 65, 85, 0.3); padding: 1rem; border-radius: 8px; }
        .metric-label { font-size: 0.875rem; color: #94a3b8; margin-bottom: 0.25rem; }
        .metric-value { font-size: 1.25rem; font-weight: 600; }
        .metric-value.warning { color: #f59e0b; } .metric-value.error { color: #ef4444; }
        .recommendations { margin-top: 1.5rem; padding-top: 1.5rem; border-top: 1px solid #334155; }
        .recommendations h3 { font-size: 1rem; margin-bottom: 1rem; color: #e2e8f0; }
        .recommendations ul { list-style: none; display: flex; flex-direction: column; gap: 0.75rem; }
        .recommendations li { display: flex; align-items: flex-start; gap: 0.75rem; color: #cbd5e1; font-size: 0.875rem; line-height: 1.4; }
        .recommendations .icon { color: #10b981; flex-shrink: 0; margin-top: 0.125rem; }
        
        .tariff-total { display: flex; justify-content: space-between; align-items: center; padding: 1.5rem; background: rgba(16, 185, 129, 0.1); border-radius: 12px; border: 1px solid rgba(16, 185, 129, 0.2); margin-bottom: 1.5rem; }
        .total-label { font-size: 1.125rem; font-weight: 500; color: #e2e8f0; } .total-value { font-size: 2rem; font-weight: 700; color: #10b981; }
        .tariff-breakdown { display: flex; flex-direction: column; gap: 1rem; margin-bottom: 1.5rem; }
        .breakdown-item { display: flex; justify-content: space-between; color: #cbd5e1; padding-bottom: 0.5rem; border-bottom: 1px solid #334155; }
        .slab-details { margin-top: 1.5rem; padding-top: 1.5rem; border-top: 1px dashed #334155; }
        .slab-details h4 { font-size: 0.875rem; color: #94a3b8; text-transform: uppercase; margin-bottom: 1rem; }
        .slab-item { display: flex; justify-content: space-between; color: #94a3b8; font-size: 0.875rem; margin-bottom: 0.5rem; }
        
        .app-footer { text-align: center; padding: 2rem; color: #64748b; font-size: 0.875rem; border-top: 1px solid #334155; margin-top: 2rem; }
      `}</style>
    </div>
  );
};

export default App;